import {
  HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import useEmblaCarousel from 'embla-carousel-react'

import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Image from '@/components/base/Image'
import Section from '@/components/base/Section'

import { cn } from '@/lib/utils'
import { WPImage } from '@/types'

export type ImageFullSliderProps = HTMLAttributes<HTMLElement> &
  Partial<{
    slides: WPImage[]
  }>

const ImageFullSlider = ({
  slides = [],
  className,
  ...props
}: ImageFullSliderProps) => {
  const [ref, emblaApi] = useEmblaCarousel({ align: 'start' })
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])
  const needsCarousel = useMemo(() => slides.length > 1, [slides.length])

  const onInit = useCallback(() => {
    if (!emblaApi) return
    setScrollSnaps(emblaApi.scrollSnapList())
  }, [emblaApi])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) return
      emblaApi.scrollTo(index)
    },
    [emblaApi]
  )

  useEffect(() => {
    if (!emblaApi) return
    emblaApi.reInit({ active: needsCarousel })
  }, [needsCarousel, emblaApi])

  useEffect(() => {
    if (!emblaApi) return

    onInit()
    onSelect()
    emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onInit, onSelect])

  return (
    <Section className={cn([className])} {...props}>
      <Container>
        <Grid className="gap-y-[2rem]">
          {slides.length > 0 ? (
            <div
              ref={ref}
              className={cn([
                'col-span-4 md:col-span-6 lg:col-span-12',
                '-mx-margin select-none overflow-hidden px-margin'
              ])}
            >
              <div className="-ml-margin flex touch-pan-y">
                {slides.map((image, index) => (
                  <div
                    key={`image-card-${index}`}
                    className="min-w-0 shrink-0 grow-0 basis-full pl-margin"
                  >
                    <div
                      className={cn([
                        'relative',
                        'aspect-h-[230] aspect-w-[350] rounded-[3rem] bg-dark-blue md:aspect-h-[750] md:aspect-w-[1320]'
                      ])}
                    >
                      <Image
                        source={image}
                        fill
                        fit="cover"
                        className="rounded-[3rem]"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {scrollSnaps.length > 1 ? (
            <div
              className={cn([
                'col-span-4 md:col-span-6 lg:col-span-12',
                'flex items-center space-x-[.3rem]'
              ])}
            >
              {scrollSnaps.map((_, index) => (
                <div key={index} className="flex shrink-0">
                  <button
                    className="-m-[.3rem] flex p-[.3rem]"
                    onClick={() => onDotButtonClick(index)}
                  >
                    <span className="sr-only">{`Slide ${index}`}</span>
                    <span
                      className={cn([
                        'flex h-[.8rem] w-[.8rem] rounded-full border-[.1rem] border-foreground',
                        'group-[.modal]:border-medium-blue',
                        {
                          'bg-foreground group-[.modal]:bg-medium-blue':
                            index === selectedIndex
                        }
                      ])}
                    />
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </Grid>
      </Container>
    </Section>
  )
}

export default ImageFullSlider
